import React from 'react';
import { Typography, COLORS, Tooltip, IconButton, Tag } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Info from '@mui/icons-material/Info';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import type { Resource } from 'types';
import { getItemTypeLabel } from 'utils/fileUtils';
import { LIBRARY_GRID_CARD_SX } from 'constants/styles';

interface ResourceGridCardProps {
  resource: Resource;
  onOpen: (resource: Resource) => void;
  onShowDetails: (resource: Resource, e?: React.MouseEvent) => void;
  onEdit: (resource: Resource) => void;
  onDelete: (resource: Resource) => void;
  canEdit: boolean;
  canDelete: boolean;
  selected?: boolean;
  /** Apply the selected border/background treatment (browse page only). */
  showSelection?: boolean;
}

const REVEAL_SX = { opacity: 0, transition: 'opacity 0.15s ease' } as const;

/** A resource card in the grid view of the browse and home pages. */
const ResourceGridCard: React.FC<ResourceGridCardProps> = ({
  resource,
  onOpen,
  onShowDetails,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  selected = false,
  showSelection = false,
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onOpen(resource)}
    onKeyDown={(e: React.KeyboardEvent) => {
      // Only act when the card itself is focused, so Enter/Space on a nested
      // Info/Edit/Delete button doesn't bubble up and also open the resource.
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen(resource);
      }
    }}
    sx={
      showSelection
        ? {
            ...LIBRARY_GRID_CARD_SX,
            border: selected ? `1.5px solid ${COLORS.purple[700]}` : `1px solid ${COLORS.gray[200]}`,
            bgcolor: selected ? 'rgba(106,27,131,0.03)' : COLORS.white,
          }
        : LIBRARY_GRID_CARD_SX
    }
  >
    <Typography
      variant="interactive-sm"
      style={{ fontWeight: 600, color: COLORS.gray[900], display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35, marginBottom: 4 }}
    >
      {resource.title}
    </Typography>
    {resource.description && (
      <Typography
        variant="p-sm"
        style={{ color: COLORS.gray[400], fontSize: 11, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4, marginBottom: 6 }}
      >
        {resource.description}
      </Typography>
    )}
    <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Tag variant="default" label={getItemTypeLabel(resource.type)} size="small" />
      <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
        <IconButton
          size="small"
          color="primary"
          className="info-reveal"
          sx={REVEAL_SX}
          onClick={(e: React.MouseEvent) => onShowDetails(resource, e)}
          aria-label={`View details for ${resource.title}`}
        >
          <Info fontSize="small" />
        </IconButton>
        {canEdit && (
          <Tooltip title="Edit resource" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" className="info-reveal" sx={REVEAL_SX} aria-label="Edit resource" onClick={() => onEdit(resource)}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete resource" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" className="info-reveal" sx={REVEAL_SX} aria-label="Delete resource" onClick={() => onDelete(resource)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  </Box>
);

export default React.memo(ResourceGridCard);
